// src/components/Booking/ReservationDetailModal.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CreditCard, MapPin, MessageCircle, Calendar, Star, X } from 'lucide-react';
import bookingService from '../../services/bookingService';
import salonService from '../../services/salonService';
import { formatPriceFCFA } from '../../utils/formatters';
import './ReservationDetailModal.css';

const ReservationDetailModal = ({ reservation, onClose, onActionComplete }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [payLoading, setPayLoading] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelError, setCancelError] = useState('');

  const formatFullDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', {
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
    });
  };

  const getEndTime = () => {
    const services = reservation.services_details
      ? (typeof reservation.services_details === 'string'
          ? JSON.parse(reservation.services_details)
          : reservation.services_details)
      : null;
    const totalDuration = services
      ? services.reduce((sum, s) => sum + (s.duration || 0), 0)
      : reservation.service_duration || 0;
    const [hours, minutes] = reservation.reservation_time.split(':').map(Number);
    const endMinutes = hours * 60 + minutes + totalDuration;
    return `${String(Math.floor(endMinutes / 60)).padStart(2, '0')}:${String(endMinutes % 60).padStart(2, '0')}`;
  };

  const formatDuration = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins  = minutes % 60;
    if (hours > 0 && mins > 0) return `${hours}h${mins}`;
    if (hours > 0) return `${hours}h`;
    return `${mins}min`;
  };

  const services = reservation.services_details
    ? (typeof reservation.services_details === 'string'
        ? JSON.parse(reservation.services_details)
        : reservation.services_details)
    : [{ name: reservation.service_name, duration: reservation.service_duration, price: reservation.total_price }];

  const totalDuration = services.reduce((sum, s) => sum + (s.duration || 0), 0);

  const getStatusBadge = (status) => {
    const badges = {
      EN_ATTENTE: { label: 'En attente',  class: 'status-pending'   },
      CONFIRMEE:  { label: 'Confirmé',    class: 'status-confirmed' },
      TERMINEE:   { label: 'Terminé',     class: 'status-completed' },
      ANNULEE:    { label: 'Annulé',      class: 'status-cancelled' },
      REFUSEE:    { label: 'Refusé',      class: 'status-refused'   },
    };
    return badges[status] || { label: status, class: 'status-default' };
  };

  const canCancel = () => {
    if (reservation.status !== 'EN_ATTENTE' && reservation.status !== 'CONFIRMEE') {
      return { allowed: false, reason: 'Cette réservation ne peut plus être annulée' };
    }
    const reservationDateTime = new Date(`${reservation.reservation_date}T${reservation.reservation_time}`);
    const hoursUntil = (reservationDateTime - new Date()) / (1000 * 60 * 60);
    if (hoursUntil < 24) {
      return { allowed: false, reason: 'Annulation impossible : moins de 24h avant le rendez-vous' };
    }
    return { allowed: true };
  };

  const handleCancel = async () => {
    setLoading(true);
    setCancelError('');
    try {
      const result = await bookingService.cancelReservation(reservation.id);
      if (result.success) {
        onActionComplete();
      } else {
        setCancelError(result.message);
      }
    } catch (error) {
      setCancelError('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const handleFinaliserPaiement = async () => {
    setPayLoading(true);
    try {
      // Récupérer escrow + infos salon en parallèle
      const [escrowResult, salon] = await Promise.all([
        bookingService.getEscrow(reservation.id),
        salonService.getSalonById(reservation.salon_id),
      ]);

      navigate('/booking/payment', {
        state: {
          reservationId:  reservation.id,
          depositPercent: escrowResult.escrow?.deposit_percentage,
          depositAmount:  escrowResult.escrow?.deposit_amount,
          totalPrice:     reservation.total_price,
          salonName:      reservation.salon_name,
          wavePhone:      salon?.wave_phone   || null,
          orangePhone:    salon?.orange_phone || null,
        }
      });
      onClose();
    } catch (error) {
      console.error('Erreur finaliser paiement:', error);
    } finally {
      setPayLoading(false);
    }
  };

  const handleContact = () => {
    navigate(`/messages?salon=${reservation.salon_id}`);
    onClose();
  };

  const handleItinerary = () => {
    const address = encodeURIComponent(`${reservation.salon_address}, ${reservation.salon_city}`);
    window.open(`https://www.google.com/maps/search/?api=1&query=${address}`, '_blank');
  };

  const handleAddToCalendar = () => {
    const title     = `RDV ${reservation.salon_name}`;
    const details   = services.map(s => s.name).join(', ');
    const location  = `${reservation.salon_address}, ${reservation.salon_city}`;
    const startDate = new Date(`${reservation.reservation_date}T${reservation.reservation_time}`);
    const endDate   = new Date(startDate.getTime() + totalDuration * 60000);
    const url = `https://www.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(title)}&dates=${startDate.toISOString().replace(/-|:|\.\d\d\d/g, '')}/${endDate.toISOString().replace(/-|:|\.\d\d\d/g, '')}&details=${encodeURIComponent(details)}&location=${encodeURIComponent(location)}`;
    window.open(url, '_blank');
  };

  const handleReview = () => {
    const servicesDetails = reservation.services_details
      ? (typeof reservation.services_details === 'string'
          ? JSON.parse(reservation.services_details)
          : reservation.services_details)
      : null;
    const serviceName = servicesDetails
      ? (servicesDetails.length === 1 ? servicesDetails[0].name : `${servicesDetails[0].name} (+${servicesDetails.length - 1})`)
      : (reservation.service_name || 'Service');

    navigate('/review', {
      state: {
        reservationId:   reservation.id,
        salonName:       reservation.salon_name,
        serviceName,
        reservationDate: reservation.reservation_date,
      }
    });
    onClose();
  };

  const status      = getStatusBadge(reservation.status);
  const cancelCheck = canCancel();
  const isPendingPayment = reservation.status === 'EN_ATTENTE' && reservation.created_by === 'CLIENT';

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content reservation-detail-modal" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="modal-header">
          <h2>{reservation.salon_name}</h2>
          <button className="close-btn" onClick={onClose}><X size={18} /></button>
        </div>

        <div className="modal-body">

          {/* Statut */}
          <div className={`status-banner ${status.class}`}>
            <span className="status-label">{status.label}</span>
          </div>

          {/* Alerte paiement */}
          {isPendingPayment && (
            <div className="payment-alert">
              <CreditCard size={16} />
              <span>Votre acompte n'a pas encore été payé. Finalisez le paiement pour confirmer votre réservation.</span>
            </div>
          )}

          {/* Lieu */}
          <div className="detail-section">
            <h3><MapPin size={15} /> Lieu</h3>
            <p className="address">{reservation.salon_address}, {reservation.salon_city}</p>
            <button className="btn-link" onClick={handleItinerary}>Voir l'itinéraire</button>
          </div>

          {/* Services */}
          <div className="detail-section">
            <h3>Services ({services.length})</h3>
            <ul className="services-list">
              {services.map((service, index) => (
                <li key={index} className="service-item">
                  <div className="service-info">
                    <span className="service-name">{service.name}</span>
                    <span className="service-duration">({formatDuration(service.duration)})</span>
                  </div>
                  <span className="service-price">{formatPriceFCFA(service.price)}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Horaire */}
          <div className="detail-section">
            <h3>Horaire</h3>
            <div className="time-info">
              <div className="time-row">
                <span className="time-label">Date :</span>
                <strong>{formatFullDate(reservation.reservation_date)}</strong>
              </div>
              <div className="time-row">
                <span className="time-label">Début :</span>
                <strong>{reservation.reservation_time}</strong>
              </div>
              <div className="time-row">
                <span className="time-label">Fin :</span>
                <strong>{getEndTime()}</strong>
              </div>
              <div className="time-row total">
                <span className="time-label">Durée totale :</span>
                <strong>{formatDuration(totalDuration)}</strong>
              </div>
            </div>
          </div>

          {/* Prix */}
          <div className="detail-section">
            <div className="price-total">
              <span>Total</span>
              <strong>{formatPriceFCFA(reservation.total_price)}</strong>
            </div>
          </div>

          {/* Politique */}
          <div className="detail-section policy">
            <p>Annulation gratuite jusqu'à 24 heures avant le rendez-vous</p>
          </div>

          {cancelError && <div className="error-message">{cancelError}</div>}

        </div>

        {/* Actions */}
        <div className="modal-footer">

          {/* EN_ATTENTE */}
          {reservation.status === 'EN_ATTENTE' && (
            <>
              {isPendingPayment && (
                <button
                  className="btn-primary full-width"
                  onClick={handleFinaliserPaiement}
                  disabled={payLoading}
                >
                  <CreditCard size={16} strokeWidth={2} />
                  {payLoading ? 'Chargement…' : 'Finaliser le paiement'}
                </button>
              )}
              <button
                className="btn-secondary full-width"
                onClick={() => setShowCancelConfirm(true)}
                disabled={loading}
              >
                Annuler la réservation
              </button>
            </>
          )}

          {/* CONFIRMEE */}
          {reservation.status === 'CONFIRMEE' && (
            <>
              <button className="btn-secondary" onClick={handleContact}>
                <MessageCircle size={15} /> Contacter
              </button>
              <button className="btn-secondary" onClick={handleAddToCalendar}>
                <Calendar size={15} /> Calendrier
              </button>
              {cancelCheck.allowed ? (
                <button className="btn-danger" onClick={() => setShowCancelConfirm(true)} disabled={loading}>
                  Annuler
                </button>
              ) : (
                <p className="cancel-disabled">{cancelCheck.reason}</p>
              )}
            </>
          )}

          {/* TERMINEE */}
          {reservation.status === 'TERMINEE' && (
            <>
              <button className="btn-secondary" onClick={handleContact}>
                <MessageCircle size={15} /> Contacter
              </button>
              <button className="btn-primary" onClick={handleReview}>
                <Star size={15} /> Laisser un avis
              </button>
            </>
          )}

        </div>

        {/* Confirmation annulation */}
        {showCancelConfirm && (
          <div className="confirm-overlay">
            <div className="confirm-dialog">
              <h3>Confirmer l'annulation ?</h3>
              <p>Cette action est irréversible</p>
              <div className="confirm-actions">
                <button className="btn-secondary" onClick={() => setShowCancelConfirm(false)} disabled={loading}>
                  Non, garder
                </button>
                <button className="btn-danger" onClick={handleCancel} disabled={loading}>
                  {loading ? 'Annulation...' : 'Oui, annuler'}
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default ReservationDetailModal;
